import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { TranscriptDisplay } from './transcript-display';
import { SpeakerAvatar } from "@/components/SpeakerAvatar";

// ── Types ──

interface WordInfo {
  text: string;
  start_ms: number;
  end_ms: number;
  confidence?: number;
}

interface Segment {
  speaker: string;
  text: string;
  timestamp?: string;
  start_ms?: number;
  end_ms?: number;
  words?: WordInfo[]; // Word-level timing for karaoke highlighting
}

interface SpeakerMeta {
  id: string;
  display_name?: string;
  name?: string;
  role?: string;
  bio?: string;
  image_url?: string;
}

interface ResultViewProps {
  title: string;
  channelName?: string;
  summary?: string;
  segments: Segment[];
  speakers: SpeakerMeta[];
  durationMs: number;
  minutesUsed?: number;
  itemId?: string;
  sourceUrl?: string;
  videoId?: string;
  onStartNew?: () => void;
}

// ── Helpers ──

function msToDuration(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) {
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

const BADGE_COLORS = [
  'bg-blue-100 text-blue-700',
  'bg-green-100 text-green-700',
  'bg-purple-100 text-purple-700',
  'bg-orange-100 text-orange-700',
  'bg-pink-100 text-pink-700',
  'bg-teal-100 text-teal-700',
];

type ExportFormat = 'txt' | 'srt' | 'vtt' | 'json';

function msToTimestamp(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

// ── Component ──

export function ResultView({
  title,
  channelName,
  summary,
  segments,
  speakers,
  durationMs,
  minutesUsed,
  itemId,
  sourceUrl,
  videoId,
  onStartNew,
}: ResultViewProps) {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const [editedSegments, setEditedSegments] = useState<Segment[]>(segments);

  const wordCount = editedSegments.reduce((acc, seg) => acc + seg.text.split(' ').length, 0);
  const speakerCount = new Set(editedSegments.map((s) => s.speaker)).size;
  const uniqueNames = Array.from(new Set(editedSegments.map((s) => s.speaker || 'Unknown')));
  const badgeColorMap = new Map(uniqueNames.map((name, i) => [name, BADGE_COLORS[i % BADGE_COLORS.length]]));

  const handleEditText = useCallback((index: number, newText: string) => {
    setEditedSegments((prev) =>
      prev.map((seg, i) => (i === index ? { ...seg, text: newText } : seg))
    );
  }, []);

  const handleEditSpeaker = useCallback((oldName: string, newName: string) => {
    setEditedSegments((prev) =>
      prev.map((seg) => (seg.speaker === oldName ? { ...seg, speaker: newName } : seg))
    );
  }, []);

  function buildMarkdown(): string {
    const names = Array.from(new Set(editedSegments.map((s) => s.speaker)));
    const wc = editedSegments.reduce((acc, s) => acc + s.text.split(' ').length, 0);

    let md = `# ${title}\n\n`;
    if (channelName) md += `**Channel:** ${channelName}\n`;
    md += `**Duration:** ${durationMs ? msToDuration(durationMs) : 'N/A'}\n`;
    md += `**Words:** ${wc}\n`;
    md += `**Speakers:** ${names.length}\n\n`;

    md += `## Speakers\n\n`;
    names.forEach((name) => {
      md += `- ${name}\n`;
    });
    md += `\n## Transcript\n\n`;
    editedSegments.forEach((seg) => {
      md += `**${seg.speaker}** _(${msToTimestamp(seg.start_ms || 0)})_\n${seg.text}\n\n`;
    });
    return md.trim();
  }

  function handleExportMarkdown() {
    const md = buildMarkdown();
    const blob = new Blob([md], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${title}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  async function handleCopy() {
    const md = buildMarkdown();
    await navigator.clipboard.writeText(md);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  function handleExport(format: ExportFormat) {
    const lines = editedSegments.map((seg) => ({
      speaker: seg.speaker,
      text: seg.text,
      timestamp: seg.timestamp || msToTimestamp(seg.start_ms || 0),
    }));

    let content = '';
    const filename = `${title}.${format}`;

    switch (format) {
      case 'txt':
        content = lines.map((l) => `${l.speaker}: ${l.text}`).join('\n\n');
        break;

      case 'srt':
        content = lines
          .map((l, i) => {
            const end = lines[i + 1]?.timestamp || msToTimestamp(durationMs);
            return `${i + 1}\n${l.timestamp},000 --> ${end},000\n${l.speaker}: ${l.text}\n`;
          })
          .join('\n');
        break;

      case 'vtt':
        content =
          'WEBVTT\n\n' +
          lines
            .map((l, i) => {
              const end = lines[i + 1]?.timestamp || msToTimestamp(durationMs);
              return `${l.timestamp}.000 --> ${end}.000\n${l.speaker}: ${l.text}`;
            })
            .join('\n\n');
        break;

      case 'json': {
        content = JSON.stringify(
          {
            transcript: lines,
            metadata: {
              duration: msToDuration(durationMs),
              wordCount,
              speakerCount,
            },
          },
          null,
          2
        );
        break;
      }
    }

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="w-full max-w-4xl mx-auto px-4 py-6 space-y-6"
    >
      {/* Header */}
      <div className="space-y-3 sm:space-y-0 sm:flex sm:flex-wrap sm:items-start sm:justify-between sm:gap-4">
        <div className="min-w-0 sm:flex-1">
          <h1 className="text-xl font-semibold text-gray-900 break-words">{title}</h1>
          <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-sm text-gray-500">
            {channelName && (
              <>
                <span className="font-medium text-gray-600">{channelName}</span>
                {(sourceUrl || videoId) && <span className="text-gray-300">&middot;</span>}
              </>
            )}
            {videoId && !videoId.startsWith('rss_') && (
              <a
                href={sourceUrl || `https://www.youtube.com/watch?v=${videoId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center text-red-500 hover:text-red-600 transition-colors"
                title="Watch on YouTube"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  className="h-3.5 w-3.5"
                >
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                </svg>
              </a>
            )}
            {videoId?.startsWith('rss_') && sourceUrl && (
              <a
                href={sourceUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-500 hover:underline"
              >
                Source
              </a>
            )}
          </div>
        </div>
        <div className="flex gap-2 shrink-0">
          {itemId && (
            <button
              onClick={() => navigate(`/transcript/${itemId}`)}
              className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              View in Library
            </button>
          )}
          {onStartNew && (
            <button
              onClick={onStartNew}
              className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700"
            >
              New Transcription
            </button>
          )}
        </div>
      </div>

      {/* Metadata */}
      <div className="flex flex-wrap gap-6 text-sm text-gray-500">
        <span>Duration: {durationMs ? msToDuration(durationMs) : 'N/A'}</span>
        <span>Words: {wordCount.toLocaleString()}</span>
        <span>Speakers: {speakerCount}</span>
        {minutesUsed !== undefined && minutesUsed > 0 && (
          <span className="text-gray-400">
            {minutesUsed % 1 === 0 ? minutesUsed : minutesUsed.toFixed(1)} min used
          </span>
        )}
      </div>

      {/* Summary */}
      {summary && (
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-gray-500">Summary</h2>
          <p className="text-sm leading-relaxed text-gray-700">{summary}</p>
        </div>
      )}

      {/* Speakers */}
      {speakerCount > 0 && (
        <div>
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">Speakers</h2>
          <div className="flex flex-wrap gap-3">
            {uniqueNames.map((name) => {
              const info = speakers.find((sp) => sp.display_name === name || sp.name === name || sp.id === name);
              const color = badgeColorMap.get(name) ?? BADGE_COLORS[0];
              return (
                <div
                  key={name}
                  className="flex items-start gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2"
                >
                  <SpeakerAvatar
                    name={name}
                    imageUrl={info?.image_url}
                    size="md"
                    className={color}
                  />
                  <div className="min-w-0">
                    <span className="text-sm font-medium text-gray-900">{name}</span>
                    {info?.role && info.role !== 'unknown' && (
                      <p className="text-xs text-gray-500">{info.role}</p>
                    )}
                    {info?.bio && <p className="text-xs text-gray-400">{info.bio}</p>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        {(['txt', 'srt', 'vtt', 'json'] as ExportFormat[]).map((fmt) => (
          <button
            key={fmt}
            onClick={() => handleExport(fmt)}
            className="rounded-md border px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
          >
            Export {fmt.toUpperCase()}
          </button>
        ))}

        <button
          onClick={handleExportMarkdown}
          className="rounded-md border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs font-medium text-blue-700 hover:bg-blue-100"
        >
          AI Ready
        </button>
        <button
          onClick={handleCopy}
          className={`rounded-md border px-3 py-1.5 text-xs font-medium ${
            copied
              ? 'border-green-300 bg-green-50 text-green-700'
              : 'border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100'
          }`}
        >
          {copied ? 'Copied!' : 'Copy'}
        </button>
        {itemId && (
          <button
            onClick={() => navigate(`/chat?item=${itemId}`)}
            className="min-h-[44px] rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 active:bg-blue-800"
          >
            💬 Chat
          </button>
        )}
      </div>

      {/* Transcript */}
      <TranscriptDisplay
        transcript={editedSegments}
        editable
        onEditText={handleEditText}
        onEditSpeaker={handleEditSpeaker}
        durationMs={durationMs}
      />
    </motion.div>
  );
}
